import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from '@/components/Sidebar';
import { MeshFilesProvider } from '@/contexts/MeshFilesContext';
import { Logo } from '@/components/Logo';
import { Menu } from 'lucide-react';

export default function App() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true,
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    if (saved !== null) return saved !== 'false';
    return typeof window !== 'undefined' ? window.innerWidth >= 768 : true;
  });
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  useEffect(() => {
    const onResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (isDesktop) {
      localStorage.setItem('sidebarOpen', isSidebarOpen.toString());
    }
  }, [isSidebarOpen, isDesktop]);

  // Close mobile drawer when switching to desktop
  useEffect(() => {
    if (isDesktop && isMobileDrawerOpen) setIsMobileDrawerOpen(false);
  }, [isDesktop, isMobileDrawerOpen]);

  return (
    <div className="h-dvh overflow-hidden bg-k-canvas">
      {isDesktop ? (
        <div className="flex h-dvh">
          <Sidebar
            isSidebarOpen={isSidebarOpen}
            onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          />
          <div className="relative flex-1 overflow-auto">
            <div className="h-full">
              <MeshFilesProvider>
                <Outlet context={{ isSidebarOpen, isDesktop }} />
              </MeshFilesProvider>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-dvh flex-col">
          {/* Mobile top bar */}
          <header className="flex h-14 flex-shrink-0 items-center justify-between border-b-2 border-brand-indigo/20 bg-brand-teal px-3 pt-safe">
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setIsMobileDrawerOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-brand-indigo transition-colors active:bg-brand-teal-soft"
            >
              <Menu className="h-5 w-5" strokeWidth={2.5} />
            </button>
            <div className="flex items-center gap-2">
              <Logo className="h-6 w-6 text-brand-indigo" />
              <span className="font-display text-base font-extrabold tracking-tight text-brand-indigo">
                AgentCAD
              </span>
            </div>
            <div className="w-10" />
          </header>

          {/* Mobile content */}
          <div className="relative min-h-0 flex-1 overflow-auto">
            <MeshFilesProvider>
              <Outlet context={{ isSidebarOpen: false, isDesktop }} />
            </MeshFilesProvider>
          </div>

          {/* Mobile drawer */}
          {isMobileDrawerOpen && (
            <>
              <div
                role="button"
                aria-label="Close menu"
                tabIndex={0}
                onClick={() => setIsMobileDrawerOpen(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setIsMobileDrawerOpen(false);
                }}
                className="fixed inset-0 z-40 bg-brand-indigo/50"
              />
              <div className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[80vw] flex-col bg-brand-teal pt-safe">
                <Sidebar
                  isSidebarOpen={true}
                  variant="mobile"
                  onClose={() => setIsMobileDrawerOpen(false)}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
